import { useState, useCallback, useMemo } from 'react';
import type { GenerateCharacterSheetRequest, GenerateCharacterSheetResponse } from '../../../../features/admin/actors/api';
import type { WizardData } from '../ActorGenerationWizard';
import { ActorGenerateButton } from '../ActorGenerateButton';
import { useCreditRates } from '../../../../features/organizations/hooks';
import {
  RESOURCE_TYPE_IMAGE_QUALITY_4K,
  RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_4K,
} from '../../../generation/state/useGenerationState';
import styles from './Steps.module.css';
import '../../../../styles/buttons.css';
import '../../../../styles/generation-common.css';

type GenerateCharacterSheetFn = (request: GenerateCharacterSheetRequest) => Promise<GenerateCharacterSheetResponse>;

type Props = {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  generateCharacterSheetApi: GenerateCharacterSheetFn;
  onGenerateSuccess?: () => void;
};

export function CharacterSheetStep({ data, onUpdate, generateCharacterSheetApi, onGenerateSuccess }: Props) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [additionalPrompt, setAdditionalPrompt] = useState('');

  // クレジットレートを取得（キャラクターシートは4K解像度。モデル別に消費ポイントを算出）
  const { data: creditRatesData } = useCreditRates();
  const findRate = useCallback(
    (resourceType: string): number | null => {
      if (!creditRatesData?.rates) return null;
      const rate = creditRatesData.rates.find(
        (r) => r.service_type === 'image_generation' && r.resource_type === resourceType
      );
      return rate?.credits_per_unit ?? null;
    },
    [creditRatesData]
  );
  const creditByModel = useMemo(
    () => ({
      'gpt-image-2.5-flare-auto': findRate(RESOURCE_TYPE_IMAGE_GPT25_FLARE_AUTO_4K),
      'gemini-3-pro-image-preview': findRate(RESOURCE_TYPE_IMAGE_QUALITY_4K),
    }),
    [findRate]
  );
  const credits = creditByModel[data.characterSheetModel];

  // キャラクターシート生成
  const handleGenerate = useCallback(async () => {
    if (!data.personImageBase64) {
      setError('人物画像がありません');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // 元画像: AI生成の結果なら素材 ID を参照で渡す (ジョブ行に base64 を残さない)。
      // アップロード画像は base64 を送り、バックエンドが課金先の組織で一時素材化する
      const result = await generateCharacterSheetApi({
        source_material_id: data.personMaterialId ?? undefined,
        source_image_base64: data.personMaterialId ? undefined : data.personImageBase64,
        additional_prompt: additionalPrompt || undefined,
        model: data.characterSheetModel,
      });

      // 履歴に追加
      const newImage = { base64: result.image_base64, mimeType: result.mime_type, materialId: result.material_id };
      const newHistory = [...data.characterSheetHistory, newImage];
      const newIndex = newHistory.length - 1;

      onUpdate({
        characterSheetBase64: result.image_base64,
        characterSheetMimeType: result.mime_type,
        characterSheetMaterialId: result.material_id,
        characterSheetHistory: newHistory,
        characterSheetIndex: newIndex,
      });

      setHasGenerated(true);
      onGenerateSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || '生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  }, [data.personImageBase64, data.personMaterialId, data.characterSheetModel, additionalPrompt, onUpdate, generateCharacterSheetApi, onGenerateSuccess]);

  return (
    <div className={styles.stepContainer}>
      {/* 修正指示プロンプト */}
      <div className={styles.formGroupFull}>
        <label className={styles.label}>修正指示プロンプト（オプション）</label>
        <textarea
          value={additionalPrompt}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
          placeholder="例: 衣装をカジュアル服にチェンジ"
          className={styles.textarea}
          rows={3}
        />
      </div>

      {/* エラー表示 */}
      {error && <div className={styles.error}>{error}</div>}

      {/* 生成ボタン。モデル選択はボタンの ▼ ドロップダウン */}
      <div className={styles.generateButtonContainer}>
        <ActorGenerateButton
          label={hasGenerated ? 'キャラクターシートを再生成' : 'キャラクターシートを生成'}
          selectedCredits={credits}
          isGenerating={isGenerating}
          onGenerate={handleGenerate}
          model={data.characterSheetModel}
          onModelChange={(model) => onUpdate({ characterSheetModel: model })}
          creditByModel={creditByModel}
          dropdownId="actor-character-sheet-generate"
        />
      </div>
    </div>
  );
}
